import { Layout, Menu, theme } from 'antd'
import {
  DashboardOutlined,
  BookOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  BarChartOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  ApiOutlined,
  MonitorOutlined,
  CodeOutlined,
  SafetyOutlined,
  GlobalOutlined,
  RobotOutlined,
  CodeSandboxOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  DiffOutlined,
  HistoryOutlined,
  DollarOutlined,
  SoundOutlined,
  KeyOutlined,
  ClusterOutlined,
  LoginOutlined,
  TrophyOutlined,
  ShopOutlined,
  AudioOutlined,
  NodeIndexOutlined,
  PartitionOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { UserRole, useAuthStore } from '../stores/authStore'
import { useTranslation } from 'react-i18next'

const { Sider } = Layout

interface SidebarProps {
  collapsed: boolean
}

const Sidebar = ({ collapsed }: SidebarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const {
    token: { colorBgContainer },
  } = theme.useToken()

  // 根据用户角色获取菜单项
  const getMenuItems = () => {
    const baseItems = [
      {
        key: '/admin/dashboard',
        icon: <DashboardOutlined />,
        label: t('admin.dashboard'),
      },
    ]

    // 创作相关菜单（作者和管理员可见）
    const creationItems = [
      {
        key: 'creation',
        icon: <BookOutlined />,
        label: t('admin.creation'),
        children: [
          {
            key: '/admin/creation/novels',
            label: t('admin.novelManagement'),
            icon: <FileTextOutlined />,
          },
          {
            key: '/admin/creation/chapters',
            label: t('admin.chapterManagement'),
            icon: <FileTextOutlined />,
          },
          {
            key: '/admin/creation/characters',
            label: t('admin.characterManagement'),
            icon: <UserOutlined />,
          },
          {
            key: '/admin/creation/worlds',
            label: t('admin.worldManagement'),
            icon: <GlobalOutlined />,
          },
          {
            key: '/admin/creation/tasks',
            label: t('admin.taskManagement'),
            icon: <BarChartOutlined />,
          },
        ],
      },
    ]

    // 管理相关菜单（管理员可见）
    const adminItems = [
      {
        key: 'management',
        icon: <TeamOutlined />,
        label: t('admin.systemManagement'),
        children: [
          {
            key: '/admin/management/users',
            label: t('admin.users'),
            icon: <UserOutlined />,
          },
          {
            key: '/admin/management/authors',
            label: t('admin.authorManagement'),
            icon: <TeamOutlined />,
          },
          {
            key: '/admin/management/novels',
            label: t('admin.novelReview'),
            icon: <BookOutlined />,
          },
          {
            key: '/admin/management/comments',
            label: t('admin.commentManagement'),
            icon: <FileTextOutlined />,
          },
        ],
      },
      {
        key: 'rewrite',
        icon: <ThunderboltOutlined />,
        label: t('admin.rewrite'),
        children: [
          {
            key: '/admin/rewrite/upload',
            label: t('admin.rewriteUpload'),
            icon: <UploadOutlined />,
          },
          {
            key: '/admin/rewrite/dashboard',
            label: t('admin.rewriteDashboard'),
            icon: <ThunderboltOutlined />,
          },
          {
            key: '/admin/rewrite/review',
            label: t('admin.rewriteReview'),
            icon: <DiffOutlined />,
          },
          {
            key: '/admin/rewrite/history',
            label: t('admin.rewriteHistory'),
            icon: <HistoryOutlined />,
          },
          {
            key: '/admin/rewrite/collect',
            label: t('admin.rewriteCollect'),
            icon: <ApiOutlined />,
          },
        ],
      },
      {
        key: 'system',
        icon: <SettingOutlined />,
        label: t('admin.system'),
        children: [
          {
            key: '/admin/system/config',
            label: t('admin.systemConfig'),
            icon: <SettingOutlined />,
          },
          {
            key: '/admin/system/monitoring',
            label: t('admin.systemMonitoring'),
            icon: <MonitorOutlined />,
          },
          {
            key: '/admin/system/logs',
            label: t('admin.systemLogs'),
            icon: <DatabaseOutlined />,
          },
          {
            key: '/admin/system/api',
            label: t('admin.apiManagement'),
            icon: <ApiOutlined />,
          },
          {
            key: '/admin/system/security',
            label: t('admin.securitySettings'),
            icon: <SafetyOutlined />,
          },
          {
            key: '/admin/system/ai-models',
            label: t('admin.aiModels'),
            icon: <RobotOutlined />,
          },
          {
            key: '/admin/system/ldap',
            label: t('admin.ldap'),
            icon: <KeyOutlined />,
          },
          {
            key: '/admin/system/tenants',
            label: t('admin.tenants'),
            icon: <ClusterOutlined />,
          },
          {
            key: '/admin/system/sso',
            label: t('admin.sso'),
            icon: <LoginOutlined />,
          },
          {
            key: '/admin/system/credits',
            label: t('admin.credits'),
            icon: <TrophyOutlined />,
          },
          {
            key: '/admin/system/marketplace',
            label: t('admin.marketplace'),
            icon: <ShopOutlined />,
          },
          {
            key: '/admin/system/tts',
            label: t('admin.tts'),
            icon: <AudioOutlined />,
          },
          {
            key: '/admin/system/content-api',
            label: t('admin.contentApi'),
            icon: <NodeIndexOutlined />,
          },
          {
            key: '/admin/system/database',
            label: t('admin.databaseConfig'),
            icon: <PartitionOutlined />,
          },
        ],
      },
      {
        key: 'monetization',
        icon: <DollarOutlined />,
        label: t('admin.monetization'),
        children: [
          {
            key: '/admin/monetization/ads',
            label: t('admin.adManagement'),
            icon: <BarChartOutlined />,
          },
          {
            key: '/admin/monetization/audiobooks',
            label: t('admin.audiobookManagement'),
            icon: <SoundOutlined />,
          },
        ],
      },
    ]

    // 根据用户角色组合菜单
    let items = [...baseItems]

    // 开发者门户菜单（所有登录用户可见）
    const devportalItems = [
      {
        key: 'devportal',
        icon: <CodeSandboxOutlined />,
        label: t('admin.devPortal'),
        children: [
          {
            key: '/dev/dashboard',
            label: t('admin.devOverview'),
            icon: <BarChartOutlined />,
          },
          {
            key: '/dev/projects',
            label: t('admin.devProjects'),
            icon: <DatabaseOutlined />,
          },
          {
            key: '/dev/api-keys',
            label: t('admin.devApiKeys'),
            icon: <ApiOutlined />,
          },
          {
            key: '/dev/documentation',
            label: t('admin.devDocs'),
            icon: <FileTextOutlined />,
          },
          {
            key: '/dev/support',
            label: t('admin.devSupport'),
            icon: <TeamOutlined />,
          },
          {
            key: '/dev/ai-management',
            label: t('admin.devAiModels'),
            icon: <MonitorOutlined />,
          },
          {
            key: '/dev/content-api',
            label: '内容 API',
            icon: <ApiOutlined />,
          },
        ],
      },
    ]

    items = [...items, ...devportalItems]
    
    if (user?.role === UserRole.WRITER || user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN) {
      items = [...items, ...creationItems]
    }
    
    if (user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN) {
      items = [...items, ...adminItems]
    }

    return items
  }

  // 处理菜单点击
  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      navigate(key)
    }
  }

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname
    const selectedKeys: string[] = []
    
    // 匹配精确路径
    if (path === '/admin/dashboard') {
      selectedKeys.push('/admin/dashboard')
    }
    
    // 匹配子路径
    if (path.startsWith('/admin/creation')) {
      selectedKeys.push('creation')
      selectedKeys.push(path)
    }
    
    if (path.startsWith('/admin/management')) {
      selectedKeys.push('management')
      selectedKeys.push(path)
    }
    
    if (path.startsWith('/admin/system')) {
      selectedKeys.push('system')
      selectedKeys.push(path)
    }

    if (path.startsWith('/admin/rewrite')) {
      selectedKeys.push('rewrite')
      selectedKeys.push(path)
    }

    if (path.startsWith('/admin/monetization')) {
      selectedKeys.push('monetization')
      selectedKeys.push(path)
    }

    if (path.startsWith('/dev/')) {
      selectedKeys.push('devportal')
      selectedKeys.push(path)
    }
    
    return selectedKeys
  }

  // 获取展开的菜单项
  const getOpenKeys = () => {
    const path = location.pathname
    const openKeys: string[] = []
    
    if (path.startsWith('/admin/creation')) {
      openKeys.push('creation')
    }
    
    if (path.startsWith('/admin/management')) {
      openKeys.push('management')
    }
    
    if (path.startsWith('/admin/system')) {
      openKeys.push('system')
    }

    if (path.startsWith('/admin/rewrite')) {
      openKeys.push('rewrite')
    }

    if (path.startsWith('/admin/monetization')) {
      openKeys.push('monetization')
    }

    if (path.startsWith('/dev/')) {
      openKeys.push('devportal')
    }
    
    return openKeys
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={256}
      style={{
        background: colorBgContainer,
        borderRight: '1px solid #f0f0f0',
        overflow: 'auto',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
      }}
    >
      {/* Logo区域 */}
      <div className="flex items-center justify-center h-16 border-b">
        {collapsed ? (
          <div className="text-2xl font-bold text-gradient">N</div>
        ) : (
          <div className="flex items-center">
            <div className="text-2xl font-bold text-gradient">NovelAI</div>
            <div className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {t('nav.admin')}
            </div>
          </div>
        )}
      </div>

      {/* 用户信息（展开时显示） */}
      {!collapsed && user && (
        <div className="p-4 border-b">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-white">
              {user.username.charAt(0).toUpperCase()}
            </div>
            <div className="ml-3">
              <div className="font-medium">{user.username}</div>
              <div className="text-gray-500 text-xs capitalize">
                {user.role}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 菜单区域 */}
      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={getOpenKeys()}
        items={getMenuItems()}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          padding: '8px 0',
        }}
        theme="light"
      />

      {/* 底部信息（展开时显示） */}
      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t text-center">
          <div className="text-xs text-gray-500">
            © 2024 NovelAI v3
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {import.meta.env.MODE === 'development' ? t('admin.envDev') : t('admin.envProd')}
          </div>
        </div>
      )}
    </Sider>
  )
}

export default Sidebar